import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"
import { SearchInput } from "components/search/SearchInput"
import { Table } from "components/tables/Table"
import { getTournamentActions, scoringModeOptions, statusOptions } from "components/tournaments/constants"
import { TournamentPresets } from "components/tournaments/TournamentPresets"

import { useProps } from "contexts/PropsContext"

import { URL } from "constants/api/requests"
import { tableTournamentColumns } from "constants/data"
import { SortOrderType } from "constants/eums"

import { TournamentService } from "services/tournaments/tournament.service"

import { Tournament } from "types/tournament.types"

import { isMicroserviceTenant } from "config/featureFlags"

export const Tournaments = () => {
  const { user } = useProps()
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<"tournaments" | "presets">("tournaments")
  const [query, setQuery] = useState<string>("")
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [scoringModeFilter, setScoringModeFilter] = useState<string>("")
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [startTournamentModal, setStartTournamentModal] = useState<Tournament | null>(null)
  const [endTournamentModal, setEndTournamentModal] = useState<Tournament | null>(null)
  const [cancelTournamentModal, setCancelTournamentModal] = useState<Tournament | null>(null)
  const [publishTournamentModal, setPublishTournamentModal] = useState<Tournament | null>(null)
  const [duplicateTournamentModal, setDuplicateTournamentModal] = useState<Tournament | null>(null)
  const [deleteTournamentModal, setDeleteTournamentModal] = useState<Tournament | null>(null)

  const handleTabChange = (key: string) => setActiveTab(key as "tournaments" | "presets")

  const handleCreate = () => {
    navigate(URL.TOURNAMENTS_CREATE)
  }

  const handleRowClick = (row: Tournament) => {
    navigate(URL.TOURNAMENT.replace(":id", row.id))
  }

  const handleDelete = async (tournament: Tournament) => {
    setCancelTournamentModal(tournament)
  }

  const handleDeleteConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.deleteTournament(tournament.id)
      toast.success("Tournament cancelled successfully!")
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error("Failed to cancel tournament!")
    }
  }

  const handlePermanentDelete = async (tournament: Tournament) => {
    setDeleteTournamentModal(tournament)
  }

  const handlePermanentDeleteConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.permanentlyDeleteTournament(tournament.id)
      toast.success(t("tournaments.deleteSuccess") as string)
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error(t("tournaments.deleteError") as string)
    }
  }

  const handleStart = async (tournament: Tournament) => {
    setStartTournamentModal(tournament)
  }

  const handlePublish = async (tournament: Tournament) => {
    setPublishTournamentModal(tournament)
  }

  const handleDuplicate = async (tournament: Tournament) => {
    setDuplicateTournamentModal(tournament)
  }

  const handleDuplicateConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.duplicateTournament(tournament.id)
      toast.success("Tournament duplicated as draft!")
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error("Failed to duplicate tournament!")
    }
  }

  const handlePublishConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.publishTournament(tournament.id)
      toast.success("Tournament published successfully!")
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error("Failed to publish tournament!")
    }
  }

  const handleStartConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.startTournament(tournament.id)
      toast.success("Tournament started successfully!")
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error("Failed to start tournament!")
    }
  }

  const handleEnd = async (tournament: Tournament) => {
    setEndTournamentModal(tournament)
  }

  const handleEndConfirm = async (tournament: Tournament) => {
    try {
      await TournamentService.endTournament(tournament.id)
      toast.success("Tournament ended successfully!")
      setTableUpdatedAt(Date.now())
    } catch (error) {
      toast.error("Failed to end tournament!")
    }
  }

  const actions = getTournamentActions({
    onView: (row) => navigate(URL.TOURNAMENT.replace(":id", row.id)),
    onEdit: (row) => navigate(URL.TOURNAMENT_EDIT.replace(":id", row.id)),
    onMonitor: (row) => navigate(URL.TOURNAMENT_MONITOR.replace(":id", row.id)),
    onPublish: handlePublish,
    onDuplicate: handleDuplicate,
    onStart: handleStart,
    onEnd: handleEnd,
    onCancel: handleDelete,
    onDelete: handlePermanentDelete
  })

  const tabs = [
    { key: "tournaments", label: t("tournaments.tabs.tournaments") },
    { key: "presets", label: t("tournaments.tabs.rulePresets") }
  ]

  // The list endpoint doesn't allow-list created_at, so match its own default.
  let defaultSortOrderBy: string | undefined
  let defaultSortOrder: SortOrderType | undefined
  if (isMicroserviceTenant()) {
    defaultSortOrderBy = "start_time"
    defaultSortOrder = SortOrderType.ASC
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.tournaments.title")} />
      {user && <WelcomeSection />}

      <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

      {activeTab === "tournaments" && (
        <>
          <div className="flex items-end gap-4">
            <div className="flex flex-1 space-x-4 max-w-3xl">
              <SearchInput placeholder={t("form.searchByName")} onChange={setQuery} />
              <DropdownMenu
                placeholder={t("form.allStatuses")}
                options={statusOptions}
                value={statusFilter}
                onChange={(ev: any) => setStatusFilter(ev.target.value)}
                rounded
              />
              <DropdownMenu
                placeholder={t("form.allModes")}
                options={scoringModeOptions}
                value={scoringModeFilter}
                onChange={(ev: any) => setScoringModeFilter(ev.target.value)}
                rounded
              />
            </div>
            <button
              className="ml-auto btn btn-primary bg-transparent text-[rgba(139,92,246,1)] border-[rgba(139,92,246,0.8)] hover:bg-[rgba(139,92,246,1)] hover:border-transparent dark:text-white hover:text-white"
              onClick={handleCreate}
            >
              {t("tournaments.createTournament")}
            </button>
          </div>

          <Table
            title={t("tournaments.tabs.tournaments")}
            handler={TournamentService.getTournamentsList}
            columns={tableTournamentColumns as any}
            filters={{ q: query, status: statusFilter, scoring_mode: scoringModeFilter }}
            defaultSortOrderBy={defaultSortOrderBy}
            defaultSortOrder={defaultSortOrder}
            onRowClick={handleRowClick}
            actions={actions}
            updatedAt={tableUpdatedAt}
          />

          <ModalConfirmAction
            data={startTournamentModal}
            handler={() => setStartTournamentModal(null)}
            text={t("tournaments.confirmStart", { name: startTournamentModal?.name })}
            actionText={t("tournaments.startTournament")}
            actionHandler={handleStartConfirm}
          />

          <ModalConfirmAction
            data={endTournamentModal}
            handler={() => setEndTournamentModal(null)}
            text={t("tournaments.confirmEnd", { name: endTournamentModal?.name })}
            actionText={t("tournaments.endTournamentEarly")}
            actionHandler={handleEndConfirm}
          />

          <ModalConfirmAction
            data={cancelTournamentModal}
            handler={() => setCancelTournamentModal(null)}
            text={t("tournaments.confirmCancel", { name: cancelTournamentModal?.name })}
            actionText={t("tournaments.cancelTournament")}
            actionHandler={handleDeleteConfirm}
          />

          <ModalConfirmAction
            data={publishTournamentModal}
            handler={() => setPublishTournamentModal(null)}
            text={t("tournaments.confirmPublish", { name: publishTournamentModal?.name })}
            actionText={t("tournaments.publishTournament")}
            actionHandler={handlePublishConfirm}
          />

          <ModalConfirmAction
            data={duplicateTournamentModal}
            handler={() => setDuplicateTournamentModal(null)}
            text={t("tournaments.confirmDuplicate", { name: duplicateTournamentModal?.name })}
            actionText={t("tournaments.duplicateTournament")}
            actionHandler={handleDuplicateConfirm}
          />

          <ModalConfirmAction
            data={deleteTournamentModal}
            handler={() => setDeleteTournamentModal(null)}
            text={t("tournaments.confirmDelete", { name: deleteTournamentModal?.name })}
            actionText={t("tournaments.deleteTournament")}
            actionHandler={handlePermanentDeleteConfirm}
          />
        </>
      )}

      {activeTab === "presets" && <TournamentPresets />}
    </div>
  )
}
